// Sentry Configuration for EachOtter Frontend
// Benannte Importe statt `import * as Sentry` (Okt 2026): Der Namensraum-Import
// samt `typeof Sentry` hielt den Bundler davon ab, ungenutzte Teile des SDK
// (u. a. das Feedback-Widget mit preact) wegzulassen.
import {
  init,
  browserTracingIntegration,
  replayIntegration,
  breadcrumbsIntegration,
  captureException,
  captureMessage as sentryCaptureMessage,
  ErrorBoundary,
  setUser as sentrySetUser,
  addBreadcrumb as sentryAddBreadcrumb,
  logger as sentryLogger,
  startSpan as sentryStartSpan,
} from '@sentry/react';

// Bekannte, erwartbare und bereits abgefangene Zustaende, die NICHT als Fehler
// nach Sentry gehoeren. Hintergrund: setupConsoleErrorTracking() unten leitet
// jeden console.error()-Aufruf an Sentry weiter. Dadurch landen voellig
// normale, behandelte Log-Ausgaben (falsches Passwort, abgelaufene/fehlende
// Session, ausgeloggter Nutzer) als eigene Issues in Sentry und uebertoenen die
// echten Bugs. Diese Zustaende sind Nutzer-/Session-normal und kein Fehler.
// Betrifft u.a. Sentry-Issues 8B, DS, AF (falsche Anmeldedaten), 9H, 20, E7
// (getUser 401 / kein Token) sowie F1, FS, FH (kein Token verfuegbar), GV/DE
// (generisches 401), HJ (Passwort-Richtlinie), HK (ungueltiger Session-Code).
const EXPECTED_NOISE_PATTERNS = [
  // Ausgeloggt / kein Token -> erwartbar auf oeffentlichen Seiten & nach Ablauf
  'kein Token verfügbar',
  'Kein Token vorhanden',
  // getUser: abgelaufene/ungueltige Session wird sauber behandelt (401/403)
  'getUser: Authentifizierungsfehler',
  'getUser: Fehler bei der Anfrage: 401',
  // Falsche Anmeldedaten -> dem Nutzer im UI angezeigt, kein Bug
  'Login fehlgeschlagen. Bitte überprüfe deine Anmeldedaten',
  // Generisches, bereits abgefangenes 401 (Token abgelaufen/fehlt) -> Axios-
  // Standardmeldung. Betrifft GV (/) und DE (getTopMatches -> /profile/discover).
  // 401 = nicht authentifiziert; wird von der App per Redirect/Token-Refresh
  // behandelt, kein Frontend-Bug. 403 bewusst NICHT gefiltert (kann echter
  // Authz-Bug/IDOR sein), ebenso 500 (Server-Fehler).
  'Request failed with status code 401',
  // Passwort-Richtlinie bei Registrierung -> Client-Validierung, dem Nutzer im
  // UI angezeigt, kein Bug (HJ)
  'Das Passwort muss mindestens einen Großbuchstaben',
  // Ungueltiger/abgelaufener Session-Code beim Beitreten -> Nutzer-Tippfehler,
  // dem Nutzer im UI angezeigt, kein Bug (HK)
  'Ungültiger oder abgelaufener Session-Code',
  // Abgelaufener/ungueltiger Auth-Token (401) -> Backend liefert den Code
  // AUTHENTICATION_ERROR mit Meldung "Authentifizierung fehlgeschlagen" und
  // Details "Ungültiger oder abgelaufener Token". Wird von der App sauber
  // behandelt (Redirect zur Login-Seite / Token-Refresh), kein Frontend-Bug.
  // Betrifft die Chat-/App-401-Kaskade HZ, HY, HX, HW, HV (und den Exception-
  // Zwilling REACT-11). Rein 401-spezifisch -> 403 (Authz/IDOR) & 500 bleiben
  // bewusst sichtbar.
  'AUTHENTICATION_ERROR',
  'Authentifizierung fehlgeschlagen',
  'Ungültiger oder abgelaufener Token'
];

// Sammelt alle durchsuchbaren Textfelder eines Sentry-Events. Wichtig: Events
// aus console.error() (captureMessage) tragen ihren Text in event.message bzw.
// event.logentry, NICHT in hint.originalException.message - der bisherige
// Filter sah nur letzteres und liess die Console-Noise durch.
const collectEventText = (event, hint) => {
  const parts = [];
  const orig = hint?.originalException;
  if (typeof orig === 'string') {
    parts.push(orig);
  } else if (orig?.message) {
    parts.push(String(orig.message));
  }
  if (event?.message) parts.push(String(event.message));
  if (event?.logentry?.message) parts.push(String(event.logentry.message));
  const values = event?.exception?.values;
  if (Array.isArray(values)) {
    values.forEach((v) => {
      if (v?.value) parts.push(String(v.value));
      if (v?.type) parts.push(String(v.type));
    });
  }
  return parts.join('\n');
};

/**
 * Entscheidet, ob ein Sentry-Event verworfen werden soll (beforeSend).
 * Exportiert, damit die Filterlogik isoliert getestet werden kann.
 * @returns {boolean} true = Event NICHT senden
 */
export const shouldDropSentryEvent = (event, hint) => {
  const text = collectEventText(event, hint);
  if (!text) return false;

  // Browser-Extension-Fehler (nicht unsere App)
  if (text.includes('chrome-extension://') || text.includes('moz-extension://')) {
    return true;
  }

  // ResizeObserver-Warnungen (harmlos)
  if (text.includes('ResizeObserver')) {
    return true;
  }

  // SVG className-Fehler (harmlos - SVG hat className als Object)
  if (text.includes('className.split')) {
    return true;
  }

  // Erwartbare, bereits abgefangene Auth-/Session-Zustaende (kein Bug)
  if (EXPECTED_NOISE_PATTERNS.some((pattern) => text.includes(pattern))) {
    return true;
  }

  return false;
};

// Release-Name aus dem Build: "eachotter-frontend@<kurzer Git-SHA>". Exportiert für Tests.
export const getReleaseName = () => {
  const sha = (process.env.REACT_APP_RELEASE || '').trim();
  if (!sha) return undefined;
  return `eachotter-frontend@${sha.slice(0, 8)}`;
};

// ios | android | web — damit sich Fehler aus der App und aus dem Browser trennen lassen.
const getPlatformTag = () => {
  try {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    if (cap && typeof cap.getPlatform === 'function') return cap.getPlatform();
  } catch {
    // ignorieren
  }
  return 'web';
};

/**
 * Initialize Sentry Error Tracking for Frontend
 *
 * DSN: Frontend-spezifischer Sentry DSN (public, OK im Code)
 * Environment: development | staging | production
 *
 * Features:
 * - Automatic Error Tracking (Exceptions, Unhandled Rejections)
 * - User Context (wer hatte den Error?)
 * - Breadcrumbs (was führte zum Error?)
 * - Performance Monitoring (langsame Komponenten)
 */
export const initSentry = () => {
  // Only initialize in production or if explicitly enabled
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isEnabled = process.env.REACT_APP_SENTRY_ENABLED === 'true';

  if (isDevelopment && !isEnabled) {
    console.log('Sentry: Skipped in development (set REACT_APP_SENTRY_ENABLED=true to enable)');
    return;
  }

  // Produktions-Builds, die gegen einen LOKALEN Server laufen (Simulator-Tests mit
  // Capacitor server.url=localhost, "npm run build" gegen localhost:5002), meldeten
  // ihre Testfehler bisher ins Produktiv-Projekt und verwässerten dort die Triage
  // (Sentry-Issues mit "Load failed (localhost:5002)" und Demo-Chats).
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const pageHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalTarget = /localhost|127\.0\.0\.1|\b192\.168\.|\b10\.\d+\./.test(backendUrl)
    || /^(localhost|127\.0\.0\.1)$/.test(pageHost);
  if (isLocalTarget && !isEnabled) {
    console.log('Sentry: Skipped, Ziel ist ein lokaler Server (set REACT_APP_SENTRY_ENABLED=true to enable)');
    return;
  }

  try {
    init({
      dsn: 'https://9d47d2a96468fc28bee3774e1b222379@o4510187379949568.ingest.de.sentry.io/4510187382636624',
      environment: process.env.NODE_ENV || 'development',

      // Release = Git-SHA des Builds (REACT_APP_RELEASE wird in den Deploy-Workflows
      // gesetzt). Ohne Release lässt sich in Sentry nicht sagen, ob ein Fehler aus
      // dem aktuellen Web-Build oder einer zwei Monate alten App-Installation kommt.
      release: getReleaseName(),

      // Performance Monitoring
      // Bewusst niedrig: das kostenlose Sentry-Kontingent ist schnell aufgebraucht,
      // und fuer eine Beta mit drei Schulen reichen Stichproben.
      tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.02 : 1.0,

      // Session Replay: KEINE Aufzeichnung normaler Sitzungen.
      // Die App wird von (teils minderjaehrigen) Schueler:innen genutzt; eine
      // Bildschirmaufnahme haette Chats, Lerntagebuch und Feedback im Klartext
      // an Sentry uebertragen. Nur noch im Fehlerfall, und dort maskiert.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,

      // Integrations
      integrations: [
        browserTracingIntegration(),
        replayIntegration({
          maskAllText: true,   // Alle Texte maskieren (DSGVO: Schuelerdaten)
          maskAllInputs: true, // Eingabefelder ebenfalls
          blockAllMedia: true  // Bilder/Videos blocken
        }),
        // Konsolen-Ausgaben NICHT als Breadcrumbs mitschneiden. Die Standard-
        // Integration hängt sonst jedes console.log samt Argumenten an das nächste
        // Event, z. B. 'Setup-Daten erhalten:' mit Name, Alter, Klasse und Bio aus
        // dem Onboarding. enableLogs:false (unten) betrifft nur das Logs-Produkt,
        // nicht diese Breadcrumbs. Klicks, Navigation und fetch/XHR bleiben erhalten.
        breadcrumbsIntegration({ console: false })
        // Note: enableLogs ist aus (DSGVO/Kontingent). console.error wird
        // weiterhin gezielt via setupConsoleErrorTracking() gemeldet und
        // laeuft dabei durch denselben beforeSend-Filter.
      ],

      // Privacy: KEINE personenbezogenen Standarddaten (IP-Adresse, User-Agent).
      // Ohne Einwilligung und ohne AVV duerfen die nicht uebertragen werden.
      sendDefaultPii: false,

      // Konsolen-Logs nicht mitschicken: sie enthalten regelmaessig Nutzerdaten
      // und zaehlen zusaetzlich gegen das Sentry-Kontingent.
      enableLogs: false,

      // Filter Errors: bekannte harmlose Fehler und erwartbare, bereits
      // abgefangene Auth-/Session-Zustaende verwerfen (siehe shouldDropSentryEvent).
      beforeSend: (event, hint) => (shouldDropSentryEvent(event, hint) ? null : event),

      // Tags für bessere Kategorisierung
      initialScope: {
        tags: {
          component: 'eachotter-frontend',
          version: process.env.REACT_APP_VERSION || '1.0.0',
          platform: getPlatformTag()
        }
      }
    });

    console.log('✅ Sentry: Error Tracking aktiviert (Frontend)', {
      environment: process.env.NODE_ENV,
      dsn: '...de.sentry.io/4510187382636624'
    });

    // 🚨 CONSOLE ERROR OVERRIDE: Automatisches Tracking von console.error() Calls
    setupConsoleErrorTracking();
  } catch (error) {
    console.error('❌ Sentry: Initialisierung fehlgeschlagen', error);
  }
};

/**
 * Überschreibt console.error() um automatisch Errors zu Sentry zu senden
 * Behält die normale Console-Funktion für Developer Tools
 */
// Deckel für die console.error-Weiterleitung: Die App hat ~500 console.error-Stellen.
// Ohne Bremse frisst ein einziger Nutzer mit kaputtem WLAN in Minuten das kostenlose
// Kontingent, und dieselbe Meldung landet hundertfach. Deshalb: jede Meldung nur
// einmal pro Sitzung, und insgesamt höchstens MAX_CONSOLE_EVENTS pro Fenster.
const CONSOLE_EVENT_WINDOW_MS = 10 * 60 * 1000;
const MAX_CONSOLE_EVENTS_PER_WINDOW = 20;
const consoleTracking = { seen: new Set(), windowStart: 0, count: 0 };

// Exportiert für Tests. Gibt true zurück, wenn das Event gesendet werden darf.
export const shouldForwardConsoleError = (message, now = Date.now()) => {
  if (now - consoleTracking.windowStart > CONSOLE_EVENT_WINDOW_MS) {
    consoleTracking.windowStart = now;
    consoleTracking.count = 0;
  }
  if (consoleTracking.count >= MAX_CONSOLE_EVENTS_PER_WINDOW) return false;
  const key = String(message).slice(0, 160);
  if (consoleTracking.seen.has(key)) return false;
  consoleTracking.seen.add(key);
  consoleTracking.count += 1;
  return true;
};

// Aus Nicht-Error-Objekten übernommene Felder. Ohne sie kam z. B. der Grund einer
// gescheiterten Push-Registrierung (Capacitor: { error }) nur als
// "[object keys: error]" an, und der Noise-Filter fand die Meldung nicht.
// Bewusst NICHT message (Benachrichtigungen tragen dort Chat-Text), name
// (Klarname in Profil-Objekten) und details (beliebiger Inhalt).
const READABLE_OBJECT_FIELDS = ['error', 'code', 'status'];

// Backend-Meldungen enthalten teils E-Mail-Adressen ("User mit Email … existiert bereits").
const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

// Argument-Kurzform für den Sentry-Kontext, gekürzt. Objekte werden bewusst
// NICHT tief geklont — darin stecken regelmäßig Feedback-Texte,
// Registrierungsdaten oder ganze Nutzerobjekte (PII von Minderjährigen).
// Exportiert für Tests.
export const describeArg = (arg) => {
  let text;
  if (arg instanceof Error) {
    text = `${arg.name}: ${arg.message}`;
  } else if (typeof arg === 'object' && arg !== null) {
    const readable = READABLE_OBJECT_FIELDS
      .filter((field) => typeof arg[field] === 'string' || typeof arg[field] === 'number')
      .map((field) => `${field}=${arg[field]}`);
    text = readable.length > 0
      ? `{${readable.join(', ')}}`
      : `[object keys: ${Object.keys(arg).slice(0, 8).join(',')}]`;
  } else {
    text = String(arg);
  }
  return text.replace(EMAIL_PATTERN, '[E-Mail]').substring(0, 200);
};

const setupConsoleErrorTracking = () => {
  const originalConsoleError = console.error;

  console.error = function(...args) {
    // Normale Console-Ausgabe beibehalten
    originalConsoleError.apply(console, args);

    // Zu Sentry senden (wenn aktiviert)
    try {
      const errorObject = args.find(arg => arg instanceof Error);
      const errorMessage = args.map(describeArg).join(' ');

      if (!shouldForwardConsoleError(errorObject ? errorObject.message : errorMessage)) {
        return;
      }

      const consoleContext = { type: 'console.error', arguments: args.map(describeArg) };

      if (errorObject) {
        // Echter Error mit Stack Trace
        captureException(errorObject, { contexts: { console: consoleContext } });
      } else {
        // Als Message (z.B. "Fehler beim Hochladen des Bildes: ...")
        sentryCaptureMessage(errorMessage, { level: 'error', contexts: { console: consoleContext } });
      }
    } catch (sentryError) {
      // Sentry-Fehler nicht erneut loggen (Endlosschleife!)
      originalConsoleError('Sentry console tracking failed:', sentryError);
    }
  };
};

// Error Boundary Component
export const SentryErrorBoundary = ErrorBoundary;

// Capture Exception with Context
export const captureError = (error, context = {}) => {
  if (typeof captureException === 'function') {
    captureException(error, {
      contexts: { extra: context }
    });
  } else {
    console.error('Sentry Error:', error, context);
  }
};

// Capture Custom Message
export const captureMessage = (message, level = 'info', context = {}) => {
  if (typeof sentryCaptureMessage === 'function') {
    sentryCaptureMessage(message, {
      level,
      contexts: { extra: context }
    });
  } else {
    console.log('Sentry Message:', message, context);
  }
};

// Set User Context
// Nur die ID: Sie ist ein Pseudonym und reicht, um "welcher Tester hatte den Fehler"
// zu beantworten (Abgleich im Admin-Dashboard). E-Mail und Name gehen ohne AVV
// mit Sentry und ohne Einwilligung minderjähriger Nutzer nicht raus.
export const setUser = (user) => {
  if (typeof sentrySetUser === 'function') {
    sentrySetUser(user?.id ? { id: String(user.id) } : null);
  }
};

// Clear User Context (bei Logout)
export const clearUser = () => {
  if (typeof sentrySetUser === 'function') {
    sentrySetUser(null);
  }
};

// Add Breadcrumb (für Error-Context)
export const addBreadcrumb = (message, category = 'custom', level = 'info', data = {}) => {
  if (typeof sentryAddBreadcrumb === 'function') {
    sentryAddBreadcrumb({
      message,
      category,
      level,
      data
    });
  }
};

// Export Sentry logger (Best Practice aus CLAUDE_sentryrules.md)
export const logger = sentryLogger ? sentryLogger : {
  trace: (...args) => console.log('[TRACE]', ...args),
  debug: (...args) => console.log('[DEBUG]', ...args),
  info: (...args) => console.info('[INFO]', ...args),
  warn: (...args) => console.warn('[WARN]', ...args),
  error: (...args) => console.error('[ERROR]', ...args),
  fatal: (...args) => console.error('[FATAL]', ...args),
  fmt: (strings, ...values) => {
    // Template literal function für strukturierte Logs
    return strings.reduce((acc, str, i) => acc + str + (values[i] || ''), '');
  }
};

// Utility: Create Performance Span (Best Practice aus CLAUDE_sentryrules.md)
export const startSpan = (options, callback) => {
  if (typeof sentryStartSpan === 'function') {
    return sentryStartSpan(options, callback);
  }
  // Fallback wenn Sentry nicht initialisiert
  return callback();
};

export default {
  initSentry,
  captureError,
  captureMessage,
  setUser,
  clearUser,
  addBreadcrumb,
  SentryErrorBoundary,
  logger,
  startSpan
};